<script setup lang="ts">
    import Task from '~~/app/task/models/Task';

    const props = withDefaults(
        defineProps<{
            task: Task;
            displayIcons?: boolean;
        }>(),
        {
            displayIcons: true,
        }
    );

    const { onContextMenu } = useContextMenu({
        eventName: 'task:task:context-menu:open',
        payload: {
            model: props.task,
        },
    });

    const usersSelect = ref(null);
    const dateSelect = ref(null);

    const fixZIndex = computed(() => usersSelect.value?.isDisplayed || dateSelect.value?.isDisplayed);

    // Add handler for td elements
    const handleCellContextMenu = (event: MouseEvent) => {
        // Stop event from bubbling to parent elements
        event.stopPropagation();
        onContextMenu(event);
    };
</script>

<template>
    <tr :class="{ 'relative z-[10]': fixZIndex }">
        <td
            class="pr-2 py-2 border-b border-gray-100"
            @contextmenu="handleCellContextMenu"
        >
            <TaskTaskItemCheck :task="task" />
        </td>
        <td
            class="w-full px-2 py-2 border-b border-gray-100 leading-none max-w-0"
            @contextmenu="handleCellContextMenu"
        >
            <TaskTaskItemTitle :task="task" />
            <slot
                name="subtitle"
                :task="task"
            >
                <div
                    v-if="task.worksite"
                    :class="$theme('dashboard.subtitle')"
                    class="flex items-center gap-1.5 min-w-0"
                >
                    <i class="fa-regular fa-helmet-safety shrink-0" />
                    <span class="truncate">
                        {{ task.worksite?.title }}
                    </span>
                </div>
                <div
                    v-if="task.contact"
                    :class="$theme('dashboard.subtitle')"
                    class="flex items-center gap-1.5 min-w-0"
                >
                    <i class="fa-regular fa-user shrink-0" />
                    <span class="truncate">
                        {{ task.contact?.displayName }}
                    </span>
                </div>
                <div
                    v-if="task.lead"
                    :class="$theme('dashboard.subtitle')"
                    class="flex items-center gap-1.5 min-w-0"
                >
                    <i class="fa-regular fa-bullseye-arrow shrink-0" />
                    <span class="truncate">
                        {{ task.lead?.title }}
                    </span>
                </div>
            </slot>
        </td>
        <td class="px-2 py-2 border-b border-gray-100">
            <TaskTaskItemUsers
                ref="usersSelect"
                :task="task"
                :is-dashboard="true"
            />
        </td>
        <td
            v-if="displayIcons"
            class="px-2 py-2 border-b border-gray-100"
        >
            <span
                class="flex items-center justify-end px-2 space-x-2 text-sm text-gray-600 transition-all cursor-pointer hover:text-black"
                @click.prevent="task.preview()"
            >
                <span v-if="task.commentsCount">
                    {{ task.commentsCount.toShortTimeString() }}
                </span>
                <i class="fa-regular fa-comments" />
            </span>
        </td>
        <td
            v-if="displayIcons"
            class="px-2 py-2 border-b border-gray-100"
        >
            <span
                class="flex items-center justify-end px-2 space-x-2 text-sm text-gray-600 transition-all cursor-pointer hover:text-black"
                @click.prevent="task.preview()"
            >
                <span v-if="task.timeActivitiesTotalDuration">
                    {{ task.timeActivitiesTotalDuration.toShortTimeString() }}
                </span>
                <i class="fa-regular fa-stopwatch" />
            </span>
        </td>
        <td class="px-2 py-2 border-b border-gray-100 whitespace-nowrap">
            <TaskTaskItemDueDate
                ref="dateSelect"
                :task="task"
            />
        </td>
    </tr>
</template>
